import { nomeDaEtapa } from '@/entities/comercial/etapa';
/**
 * A planilha da exportação de clientes — do JSON da API para um arquivo que o
 * Excel abre. A montagem genérica e o `baixar` estão em `shared/lib/planilha.ts`.
 *
 * ── Por que a planilha é montada AQUI, e não no servidor ──────────────────
 *
 * Porque o que precisa ser controlado é a LEITURA, e ela já foi: a rota exige
 * o papel `cliente:exportar` e o step-up, e grava uma linha em
 * `core.data_access_log` por cliente, na mesma transação. Depois disso, o
 * formato é apresentação — e apresentação é da tela.
 *
 * Servir o arquivo pronto obrigaria a rota a sair do contrato JSON que o resto
 * do produto usa: outro tipo de resposta, outro caminho no cliente tipado,
 * outro jeito de tratar erro.
 *
 * ── `.xlsx`, e não mais CSV (13/10/2026) ──────────────────────────────────
 *
 * O CSV abria com o programa associado a `.csv` — o VS Code, em máquina de
 * quem o tem instalado. O `.xlsx` abre no Excel (ver `xlsx.ts`). As montagens
 * daqui devolvem a TABELA, com número como número; quem a transforma em
 * arquivo é o `baixar`.
 */

import type { ClienteExportado } from '@cliqcrm/contracts';
import { planilha, valorNaPlanilha, type Tabela } from '@/shared/lib/planilha';

const COLUNAS = [
  'Nome',
  'Telefone',
  'E-mail',
  'Responsável',
  'Etiquetas',
  'Etapa',
  'Valor',
  'Oportunidades abertas',
  'Último contato',
  'Tem documento',
] as const;

function data(iso: string | null): string {
  if (!iso) return '';
  const instante = new Date(iso);
  return Number.isNaN(instante.getTime()) ? '' : instante.toLocaleDateString('pt-BR');
}

/**
 * As colunas fixas e, depois delas, uma por campo personalizado (#568), com o
 * nome que a organização deu. O valor já vem por extenso do servidor.
 */
export function planilhaDeClientes(
  itens: readonly ClienteExportado[],
  campos: readonly { readonly id: string; readonly nome: string }[] = [],
): Tabela {
  return planilha(
    [...COLUNAS, ...campos.map((campo) => campo.nome)],
    itens.map((item) => [
      item.nome,
      item.telefone,
      item.email,
      item.responsavel,
      item.etiquetas.join(', '),
      // O nome que se lê, e não o valor gravado (`F_AGENDAMENTO_NOVO`).
      nomeDaEtapa(item.etapa_nome ?? item.etapa) || null,
      valorNaPlanilha(item.valor_micros),
      item.oportunidades_abertas,
      data(item.ultima_interacao_em),
      item.tem_documento ? 'sim' : 'não',
      ...campos.map((campo) => item.campos[campo.id] ?? ''),
    ]),
  );
}

/** `clientes-2026-09-09.xlsx` — a data no nome, porque a planilha envelhece. */
export function nomeDoArquivo(agora = new Date()): string {
  const dia = agora.toISOString().slice(0, 10);
  return `clientes-${dia}.xlsx`;
}
